import { AjaxStore, CollectionFilterConfig } from "@bryntum/core-thin";
import { RealTimeDataStore } from "@app/services/store/real-time-data-store";
import moment from "moment";
import { BaseEntity } from "@app/models/rest/base-entity";
import {
  HateoasResourceService,
  Resource,
  Sort
} from "@lagoshny/ngx-hateoas-client";
import { ClientConfigService } from "@app/core/config/client-config.service";

export class CustomAjaxStore<T extends Resource> extends AjaxStore {
  private static clientConfigService: ClientConfigService;
  private static hateoasResourceService: HateoasResourceService;
  parentId: number;
  parentField: string;
  parentResource: BaseEntity;
  parentRel: string;
  changes: { added: []; modified: []; removed: [] };
  currentPage: number = 1;
  pageSize: number;
  remoteTotal: number;
  fixedFilters: any[];
  private currentRequestId: number = 0;

  constructor(
    config: any,
    public halStore: RealTimeDataStore<T>
  ) {
    super(config);

    if (config.parentId && config.parentField) {
      this.parentId = config.parentId;
      this.parentField = config.parentField;
    }
  }

  // Static methods to set required services
  static setClientConfigService(clientConfigService: ClientConfigService) {
    CustomAjaxStore.clientConfigService = clientConfigService;
  }

  static setHateoasResourceService(
    hateoasResourceService: HateoasResourceService
  ) {
    CustomAjaxStore.hateoasResourceService = hateoasResourceService;
  }

  /**
   * Helper method to get the HATEOAS reference URL for a resource
   * This creates the proper format that Spring Data REST expects
   */
  private getHateoasReference(
    resourceObj: any,
    resourceType: string
  ): string | null {
    if (!resourceObj) {
      return null;
    }

    // First try to use the self link if available
    if (
      resourceObj._links &&
      resourceObj._links.self &&
      resourceObj._links.self.href
    ) {
      // Remove any URL parameters if present
      return resourceObj._links.self.href.split("{")[0];
    }

    if (
      resourceObj.data?._links &&
      resourceObj.data?._links.self &&
      resourceObj.data?._links.self.href
    ) {
      // Remove any URL parameters if present
      return resourceObj.data?._links.self.href.split("{")[0];
    }

    // If no self link but we have an ID and know the resource type
    if (typeof resourceObj === "object" && resourceObj.id && resourceType) {
      // Get service URL from config, fallback to a default if not available
      const serviceUrl =
        CustomAjaxStore.clientConfigService?.config?.serviceUrl ||
        "http://localhost:8080/scheduler";
      console.log("Setting ID", resourceObj.id, typeof resourceObj.id);
      //if id contains _generatedModel then return null (Bryntum auto-generates IDs for records with null id)
      if (
        typeof resourceObj.id === "string" &&
        resourceObj.id.includes("_generatedModel")
      ) {
        console.log("Using null for value");
        return null;
      }
      return `${serviceUrl}/${resourceType}/${resourceObj.id}`;
    }

    // If it's already a string URL, just return it
    if (typeof resourceObj === "string" && resourceObj.includes("http")) {
      return resourceObj;
    }

    // Fall back to just returning the ID for backward compatibility
    return resourceObj.id || null;
  }

  afterConstruct(config) {
    // do nothing
  }

  // Called when the sort is changed on the grid
  performSort(silent) {
    const me = this;
    console.log("performing sort with sorters:", me.sorters);

    // The sorters collection should already be populated by the grid
    // Let's log them to verify they're set correctly
    if (me.sorters && me.sorters.length > 0) {
      me.sorters.forEach((sorter) => {
        console.log(
          `Sort by ${sorter.field} ${sorter.ascending ? "ASC" : "DESC"}`
        );
      });
    } else {
      console.warn("No sorters defined, sorting may not work correctly");
    }

    return this.internalLoad({}, "", (event) => {
      me.data = event.data;
      // Don't trigger sort event to avoid infinite loop
      // The sort has already been performed
    });
  }

  // Called when the filter is changed on the grid
  performFilter(silent) {
    const me = this;
    me.currentPage = 1;
    const oldCount = me.count,
      { filters } = me;
    console.log("performing filter", filters);
    return this.internalLoad({}, "", (event) => {
      me.data = event.data;
      // Don't trigger filter event to avoid infinite loop
      // The filter has already been performed
    });
  }

  filter(
    newFilters:
      | {
          replace?: boolean;
          silent?: boolean;
          filters?:
            CollectionFilterConfig | CollectionFilterConfig[] | Function;
        }
      | CollectionFilterConfig
      | CollectionFilterConfig[]
      | Function
  ): Promise<any> {
    console.log("Applying filters");
    const me = this;
    me.currentPage = 1;
    const oldCount = me.count,
      { filters } = me;
    return this.internalLoad({}, "", (event) => {
      me.data = event.data;
      this.trigger("filter", event);
    });
    // return super.filter(newFilters);
  }

  // called when first loaded or changing pages
  load(params?): Promise<object> {
    const me = this;
    if (!me.currentPage) {
      me.currentPage = 1;
    }
    return this.internalLoad(params, "", (event) => {
      me.data = event.data;
    }) as Promise<object>;
  }

  // included to prevent default calls
  loadPage(page, params) {
    // not sure if needed?
    return null;
  }

  // @ts-ignore
  commit() {
    console.log("committing");
    const me = this,
      changes = me.changes,
      allPromises = [];

    // not allowing additional commits while in progress
    // TODO: should queue
    // @ts-ignore
    if (me.commitPromise) {
      return false;
    }

    // No outstanding changes, return a Promise that resolves immediately.
    if (!changes) {
      console.log("no changes, instantly resolving");
      return new Promise<void>((resolve) => {
        resolve();
      });
    }

    console.log("changes", changes.added, changes.modified, changes.removed);
    // Flag all affected records as being committed
    // @ts-ignore
    [...changes.added, ...changes.modified, ...changes.removed].forEach(
      (record) => {
        // @ts-ignore
        record.meta.committing = true;
      }
    );

    // TODO: do we need a general way of disabling plugins?
    // @ts-ignore
    if (!me.disabled && me.trigger("beforeCommit", { changes }) !== false) {
      let commitState = {
          action: "commit",
          exception: false,
          changes
        },
        p = me.commitRemoved(commitState);

      // @ts-ignore
      if (p) {
        allPromises.push(p);
      }
      // @ts-ignore
      p = me.commitAdded(commitState);
      // @ts-ignore
      if (p) {
        allPromises.push(p);
      }
      p = me.commitModified(commitState);
      // @ts-ignore
      if (p) {
        allPromises.push(p);
      }

      // If there were no urls configured, behave as a local store
      if (!allPromises.length) {
        // @ts-ignore
        me.modified.forEach((r) => r.clearChanges(false));
        // @ts-ignore
        me.modified.clear();

        // @ts-ignore
        me.added.forEach((r) => r.clearChanges(false));
        // @ts-ignore
        me.added.clear();

        // @ts-ignore
        me.removed.clear();
        me.trigger("commit", { changes });
        return new Promise<void>((resolve) => {
          resolve();
        });
      }

      console.log(`awaiting ${allPromises.length} promises`);
      p = allPromises.length === 1 ? allPromises[0] : Promise.all(allPromises);

      console.log("await complete");
      // @ts-ignore
      return (me.commitPromise = new Promise((resolve, reject) => {
        // @ts-ignore
        p.then(() => {
          console.log("all promises commit completed");
          // @ts-ignore
          me.commitPromise = null;
          if (commitState.exception) {
            me.trigger("exception", commitState);
            reject(commitState);
          } else {
            me.trigger("commit", { changes });
            resolve(commitState);
          }
        }).catch(() => {
          // @ts-ignore
          me.commitPromise = null;
          reject(commitState);
        });
      }));
    }
  }

  commitAdded(commitState) {
    const me = this;
    // @ts-ignore
    const added = me.added;
    const event = { action: "create", params: {} };
    if (!added.count) {
      return null;
    }
    return new Promise((resolve) => {
      console.log("preparing to post");
      const toAdd = added.map((r) => {
        const data = { ...r.persistableData };
        delete data.filterPriorities;

        // Prevent segments array from being sent when adding cyclical settings
        // This should only be handled by the dedicated segment editor popup
        if (data.segments) {
          delete data.segments;
        }

        // For newly added records, convert objects to IDs
        // Backend expects IDs not URLs or expanded objects

        // Generic entity reference processing for all known relationship types
        const entityMappings = {
          resource: "resources",
          skill: "skills",
          zone: "zones",
          mappingPattern: "resourceFactMappingPatterns",
          subDivision: "subDivisions"
        };

        // Process all possible entity relationships
        Object.entries(entityMappings).forEach(([property, resourceType]) => {
          if (data[property] && typeof data[property] === "object") {
            console.log(`Original ${property}:`, data[property]);
            const ref = this.getHateoasReference(data[property], resourceType);
            console.log(`Converting ${property} to HATEOAS reference:`, ref);
            data[property] = ref;
          }
        });

        // Look for any other object properties that might be entities (future-proofing)
        Object.entries(data).forEach(([key, value]) => {
          // Type guard to check if this is an entity-like object with an id
          const isEntityLike = (obj: any): obj is { id: number | string } => {
            return (
              obj &&
              typeof obj === "object" &&
              "id" in obj &&
              !Array.isArray(obj)
            );
          };

          if (!entityMappings[key] && isEntityLike(value)) {
            console.log(
              `Found potential entity reference in property ${key}:`,
              value
            );
            // Try to infer resource type from key name
            const resourceType = key.endsWith("s") ? key : `${key}s`;
            let ref = this.getHateoasReference(value, resourceType);
            if (ref === null)
              this.getHateoasReference(value["data"], resourceType);
            console.log(
              `Converting ${key} to inferred HATEOAS reference:`,
              ref
            );

            data[key] = ref;
          }
        });

        return data;
      });
      commitState.create = event;
      if (this.parentRel && this.parentResource) {
        toAdd.forEach((val) => {
          val[this.parentRel] = this.parentResource;
        });
      }
      me.trigger("beforeRequest", event);
      this.halStore
        .batchCreate(toAdd)
        .then((success) => {
          if (success) {
            added.clear();
            me.trigger("commitAdded", {});
            me.trigger("refresh", event);
            resolve(commitState);
            me.trigger("afterRequest", event);
          } else {
            added.forEach((r) => (r.meta.committing = false));
            // @ts-ignore
            commitState.exception = event.exception = true;

            // @ts-ignore
            commitState.exceptionType = event.exceptionType = "server";

            me.trigger("exception", event);
            resolve(commitState);

            me.trigger("afterRequest", event);
          }
        })
        .catch((reason) => {
          added.forEach((r) => (r.meta.committing = false));
          // @ts-ignore
          commitState.exception = event.exception = true;

          // @ts-ignore
          commitState.exceptionType = event.exceptionType = "server";
          commitState.exceptionReason = reason;
          me.trigger("exception", event);
          resolve(commitState);

          me.trigger("afterRequest", event);
        });
    });
  }

  commitModified(commitState) {
    console.log("committing modified");
    const me = this;
    // @ts-ignore
    const modified = me.modified;
    const event = { action: "update", params: {} };
    if (!modified.count) {
      return null;
    }
    return new Promise((resolve) => {
      console.log("preparing to post", modified.items);

      let toUpdate = [];
      modified.forEach((value) => {
        const data = { ...value.persistableData };
        delete data.filterPriorities;

        // Prevent segments array from being sent when editing cyclical settings
        // This should only be handled by the dedicated segment editor popup
        if (data.segments) {
          delete data.segments;
        }

        // Generic entity reference processing for all known relationship types
        const entityMappings = {
          resource: "resources",
          skill: "skills",
          zone: "zones",
          mappingPattern: "resourceFactMappingPatterns",
          subDivision: "subDivisions"
        };

        // Process all possible entity relationships
        Object.entries(entityMappings).forEach(([property, resourceType]) => {
          if (data[property] && typeof data[property] === "object") {
            console.log(`Original ${property}:`, data[property]);
            const ref = this.getHateoasReference(data[property], resourceType);
            console.log(`Converting ${property} to HATEOAS reference:`, ref);
            data[property] = ref;
          }
        });

        // Look for any other object properties that might be entities (future-proofing)
        Object.entries(data).forEach(([key, value]) => {
          // Type guard to check if this is an entity-like object with an id
          const isEntityLike = (obj: any): obj is { id: number | string } => {
            return (
              obj &&
              typeof obj === "object" &&
              "id" in obj &&
              !Array.isArray(obj)
            );
          };

          if (!entityMappings[key] && isEntityLike(value)) {
            console.log(
              `Found potential entity reference in property ${key}:`,
              value
            );
            // Try to infer resource type from key name
            const resourceType = key.endsWith("s") ? key : `${key}s`;
            const ref = this.getHateoasReference(value, resourceType);
            console.log(
              `Converting ${key} to inferred HATEOAS reference:`,
              ref
            );
            data[key] = ref;
          }
        });

        // Restore HAL _links from the raw record data so that patchResource()
        // can extract the self-link URL. persistableData strips _links, but
        // the underlying Bryntum record.data still holds the full API response.
        if (value.data?._links) {
          data._links = value.data._links;
        }

        toUpdate.push(data);
      });

      // Log the transformed data
      console.log(
        "final update map",
        JSON.stringify(toUpdate, (key, value) => {
          // Special logging for debugging HATEOAS reference objects
          if (
            key === "resource" ||
            key === "skill" ||
            key === "mappingPattern"
          ) {
            console.log(`HATEOAS Reference Check - ${key}:`, value);
          }
          return value;
        })
      );
      commitState.update = event;
      me.trigger("beforeRequest", event);
      this.halStore
        .batchUpdate(toUpdate)
        .then((success) => {
          if (success) {
            modified.clear();
            me.trigger("commitAdded", {});
            me.trigger("refresh", event);
            resolve(commitState);
            me.trigger("afterRequest", event);
          } else {
            modified.forEach((r) => (r.meta.committing = false));
            // @ts-ignore
            commitState.exception = event.exception = true;

            // @ts-ignore
            commitState.exceptionType = event.exceptionType = "server";

            me.trigger("exception", event);
            resolve(commitState);

            me.trigger("afterRequest", event);
          }
        })
        .catch((reason) => {
          modified.forEach((r) => (r.meta.committing = false));
          // @ts-ignore
          commitState.exception = event.exception = true;

          // @ts-ignore
          commitState.exceptionType = event.exceptionType = "server";
          commitState.exceptionReason = reason;
          me.trigger("exception", event);
          resolve(commitState);

          me.trigger("afterRequest", event);
        });
    });
  }

  commitRemoved(commitState) {
    console.log("committing removed");
    const me = this;
    // @ts-ignore
    const removed = me.removed;
    const event = { action: "remove", params: {} };
    if (!removed.count) {
      return null;
    }
    return new Promise((resolve) => {
      console.log("preparing to post", removed.items);

      let toRemove = [];
      removed.forEach((value) => {
        toRemove.push(value.persistableData);
      });
      console.log("final delete map", JSON.stringify(toRemove));
      commitState.update = event;
      me.trigger("beforeRequest", event);
      this.halStore
        .batchDelete(toRemove)
        .then((success) => {
          if (success) {
            console.log("successfully batch removed", commitState);

            removed.clear();
            me.trigger("commitRemoved", {});
            me.trigger("refresh", event);
            resolve(commitState);
            me.trigger("afterRequest", event);
          } else {
            removed.forEach((r) => (r.meta.committing = false));
            // @ts-ignore
            commitState.exception = event.exception = true;

            // @ts-ignore
            commitState.exceptionType = event.exceptionType = "server";

            me.trigger("exception", event);
            resolve(commitState);

            me.trigger("afterRequest", event);
          }
        })
        .catch((reason) => {
          removed.forEach((r) => (r.meta.committing = false));
          // @ts-ignore
          commitState.exception = event.exception = true;

          // @ts-ignore
          commitState.exceptionType = event.exceptionType = "server";
          commitState.exceptionReason = reason;
          me.trigger("exception", event);
          resolve(commitState);

          me.trigger("afterRequest", event);
        });
    });
  }

  // actual loading implementation, called from multiple places
  internalLoad(params, eventName, callback) {
    const me = this;
    const event = { action: "read" + eventName, params: params, data: [] };

    // Generate a unique request ID for this request
    const requestId = ++this.currentRequestId;
    console.log(
      `Starting request ID ${requestId} with ${this.filters.count} filters`
    );

    return new Promise((resolve, reject) => {
      // @ts-ignore
      me._isLoading = true;

      // This may look redundant, but it allows for two levels of event listening.
      // Granular, where the observer observes only the events of interest, and
      // catch-all, where the observer is interested in all requests.
      me.trigger(`load${eventName}Start`, event);
      me.trigger("beforeRequest", event);

      //type CollectionCompareOperator =
      // '='|'!='|'>'|'>='|'<'|'<='|'*'|
      // 'startsWith'|'endsWith'|'isIncludedIn'|'isNotIncludedIn'|'includes'|'doesNotInclude'|
      // 'empty'|'notEmpty'|
      // 'between'|'notBetween'|'sameDay'|'isToday'|'isTomorrow'|'isYesterday'|'isThisWeek'|'isLastWeek'|'isNextWeek'|'isThisMonth'|'isLastMonth'|'isNextMonth'|'isThisYear'|'isLastYear'|'isNextYear'|'isYearToDate'|
      // 'isTrue'|'isFalse'

      let searchBody = [];
      console.log("Preparing to apply " + this.filters + " filters");
      (this.filters as any).forEach((filter, index) => {
        console.log(`Processing filter ${index}`, filter);
        // An incomplete Between filter (second bound not picked yet) cannot
        // be evaluated server side; skip it until both bounds are set.
        if (
          filter.operator === "between" &&
          (!Array.isArray(filter.value) ||
            filter.value.length < 2 ||
            filter.value.some((entry) => entry === null || entry === undefined))
        ) {
          return;
        }
        // translate operator string
        let operatorString = "Contains";
        switch (filter.operator) {
          case "isIncludedIn":
            operatorString = "Equals";
            break;
          case "*":
            operatorString = "Contains";
            break;
          case "!*":
            operatorString = "NotContains";
            break;
          case "<":
            operatorString = "LessThan";
            break;
          case ">":
            operatorString = "GreaterThan";
            break;
          case "empty":
            operatorString = "IsNull";
            break;
          case "=":
            operatorString = "Equals";
            break;
          case "!=":
            operatorString = "NotEquals";
            break;
          case "isToday":
            operatorString = "IsToday";
            break;
          case "isYesterday":
            operatorString = "IsYesterday";
            break;
          case "isTomorrow":
            operatorString = "IsTomorrow";
            break;
          case "isThisWeek":
            operatorString = "IsThisWeek";
            break;
          case "isLastWeek":
            operatorString = "IsLastWeek";
            break;
          case "isNextWeek":
            operatorString = "IsNextWeek";
            break;
          case "isThisMonth":
            operatorString = "IsThisMonth";
            break;
          case "isLastMonth":
            operatorString = "IsLastMonth";
            break;
          case "isNextMonth":
            operatorString = "IsNextMonth";
            break;
          case "isThisYear":
            operatorString = "IsThisYear";
            break;
          case "isLastYear":
            operatorString = "IsLastYear";
            break;
          case "isNextYear":
            operatorString = "IsNextYear";
            break;
          case "between":
            operatorString = "Between";
            break;
        }
        if (filter.value && filter.value.constructor === Date) {
          console.log("filtering a date");
          operatorString =
            operatorString === "Contains" ? "Equals" : operatorString;
          filter.value = moment(filter.value)
            .add(1, "hour")
            .parseZone()
            .toDate();
        }
        if (filter.value && filter.value.constructor === Number) {
          operatorString =
            operatorString === "Contains" ? "Equals" : operatorString;
        }
        if (filter.value !== null && filter.value !== undefined) {
          // Enum, Long, Integer, String, Date, Double
          // add to the search condition list
          console.log("preparing to split filter value", filter.value);
          const splitFilter = filter.value.split
            ? filter.value.split(",")
            : [filter.value];
          for (let i = 0; i < splitFilter.length; i++) {
            let filterValue = splitFilter[i].trim
              ? splitFilter[i].trim()
              : splitFilter[i];

            // Date instances inside an array (e.g. the two Between bounds)
            // get destroyed by the hateoas client's resolveValues, which
            // recurses into array elements and reduces a Date to an empty
            // object. Send them as ISO strings instead, normalised to start
            // of day because the server treats Between bounds as
            // day-granularity dates.
            if (Array.isArray(filterValue)) {
              filterValue = filterValue.map((entry) =>
                entry instanceof Date
                  ? moment(entry).startOf("day").toISOString()
                  : entry
              );
            }

            // Handle multiselect boolean filters
            if (
              filter.property === "followOnRequired" ||
              filter.property === "followOnActioned"
            ) {
              // Skip null values for boolean fields (equivalent to no filter)
              if (filterValue === null || filterValue === "null") {
                continue;
              }

              // Convert string boolean values to actual booleans for boolean fields
              if (filterValue === "true" || filterValue === "false") {
                filterValue = filterValue === "true";
              }
            }

            searchBody.push({
              key: filter.property,
              operation: operatorString,
              value: filterValue
            });
          }
          console.log("prepared search", searchBody);
        } else {
          searchBody.push({
            key: filter.property,
            operation: operatorString
          });
        }
      });
      if (this.fixedFilters) {
        this.fixedFilters.forEach((value) => {
          searchBody.push({
            key: value.property,
            operation: value.operator,
            value: value.value
          });
        });
      }
      let sort: Sort = {};
      if (this.parentField && this.parentId) {
        searchBody.push({
          key: this.parentField,
          operation: "Equals",
          value: this.parentId
        });
      }

      if (me.isSorted) {
        console.log("Store is sorted, processing sorters");
        me.sorters.forEach((value: any) => {
          console.log(
            `Adding sort: ${value.field} -> ${value.ascending ? "ASC" : "DESC"}`
          );
          sort[value.field] = value.ascending ? "ASC" : "DESC";
        });
      } else {
        console.log("Store is not sorted (isSorted flag is false)");
      }

      // Log the final sort object to see what's being sent
      console.log("Final sort parameters:", sort);
      this.halStore
        .advancedSearch(
          {
            pageParams: {
              page: me.currentPage - 1,
              size: me.pageSize
            },
            params: {
              projection: "detail"
            },
            sort: sort
          },
          searchBody
        )
        .then(
          //this.halStore.advancedSearch(me.currentPage - 1, me.pageSize, "detail", sortText, params, searchBody).then(
          (value) => {
            // Only process this response if it matches the current request ID
            if (requestId !== this.currentRequestId) {
              console.log(
                `Ignoring stale response from request ID ${requestId}, current is ${this.currentRequestId}`
              );
              resolve(event); // Resolve but don't update the UI
              return;
            }

            if (value) {
              console.log(
                `Processing response for request ID ${requestId} with ${searchBody.length} filters`
              );
              event.data = value.resources ? (value.resources as T[]) : [];
              me.remoteTotal = value.totalElements;
            } else {
              event.data = [];
              me.remoteTotal = 0;
            }
            callback(event);
            me.trigger("load" + eventName, event);
            resolve(event);
            me.trigger("afterRequest", event);
          },
          (reason) => {
            // Only process errors for the current request
            if (requestId !== this.currentRequestId) {
              resolve(event); // Resolve but don't update the UI
              return;
            }

            // @ts-ignore
            me._isLoading = false;
            me.remoteTotal = 0;
            event.data = [];
            callback(event);
            reject(reason);

            me.trigger("afterRequest", event);
          }
        )
        .catch((reason) => {
          // @ts-ignore
          me._isLoading = false;
          me.trigger("afterRequest", event);
        });
    });
  }
}
